/**
 * AgentSelector Component
 * Dropdown to select which agent to use for chat testing
 */

import React, { useState, useEffect } from 'react';
import { FiCpu, FiChevronDown } from 'react-icons/fi';
import { api } from '../../../../utils/api';

interface Agent {
  id: string;
  name: string;
  description?: string;
}

interface AgentSelectorProps {
  selectedAgentId?: string;
  onSelect: (agentId: string | undefined) => void;
  assistantId?: string;
  className?: string;
}

export const AgentSelector: React.FC<AgentSelectorProps> = ({
  selectedAgentId,
  onSelect,
  assistantId,
  className = ''
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Load agents
  useEffect(() => {
    const loadAgents = async () => {
      if (!assistantId) return;

      setLoading(true);
      try {
        const response = await api.get(`/agents?assistantId=${assistantId}`);
        setAgents(response.data.agents || []);
      } catch (error) {
        console.error('Failed to load agents:', error);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

    loadAgents();
  }, [assistantId]);

  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  const handleSelect = (agentId: string | undefined) => {
    onSelect(agentId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading || agents.length === 0}
        className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FiCpu size={16} />
        <span className="text-sm">
          {loading
            ? 'Loading agents...'
            : selectedAgent
            ? selectedAgent.name
            : 'Default Agent'
          }
        </span>
        <FiChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 overflow-hidden">
            {/* Default option */}
            <button
              onClick={() => handleSelect(undefined)}
              className={`w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors ${
                !selectedAgentId ? 'bg-slate-700/50' : ''
              }`}
            >
              <div className="font-medium text-white">Default Agent</div>
              <div className="text-xs text-gray-400 mt-1">
                Use the standard assistant configuration
              </div>
            </button>

            {/* Divider */}
            {agents.length > 0 && <div className="border-t border-slate-700" />}

            {/* Agent options */}
            {agents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => handleSelect(agent.id)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors ${
                  selectedAgentId === agent.id ? 'bg-slate-700/50' : ''
                }`}
              >
                <div className="font-medium text-white">{agent.name}</div>
                {agent.description && (
                  <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                    {agent.description}
                  </div>
                )}
              </button>
            ))}

            {/* No agents message */}
            {agents.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-400">
                No custom agents available
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AgentSelector;
